/**
 * Subscription Modal Utilities
 * 
 * This file provides utility functions to trigger the subscription modal
 * from anywhere in the application without needing to use the hook.
 * 
 * Note: These functions use a singleton event emitter pattern.
 */

type SubscriptionModalOptions = {
  title?: string;
  message?: string;
  feature?: string;
  showClose?: boolean;
  onUpgrade?: () => void;
  onClose?: () => void;
  primaryActionLabel?: string;
  primaryActionType?: 'upgrade' | 'contact-support';
  chatbotId:string
};

type SubscriptionModalListener = (options?: SubscriptionModalOptions) => void;

class SubscriptionModalManager {
  private listeners: SubscriptionModalListener[] = [];

  subscribe(listener: SubscriptionModalListener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  show(options?: SubscriptionModalOptions) {
    this.listeners.forEach(listener => listener(options));
  }
}

export const subscriptionModalManager = new SubscriptionModalManager();

/**
 * Show the global subscription modal from anywhere in the app
 * 
 * @example
 * ```ts
 * import { showSubscriptionModal } from '@/utils/subscriptionModal.utils';
 * 
 * // Basic usage - non-closeable modal
 * showSubscriptionModal();
 * 
 * // With custom message
 * showSubscriptionModal({
 *   title: 'Premium Feature',
 *   message: 'This feature requires a premium subscription.',
 *   feature: 'Advanced Analytics'
 * });
 * 
 * // With close button
 * showSubscriptionModal({
 *   title: 'Upgrade Required',
 *   message: 'You have reached your plan limit.',
 *   showClose: true,
 *   onClose: () => console.log('Modal closed')
 * });
 * 
 * // With custom upgrade handler
 * showSubscriptionModal({
 *   message: 'Upgrade to unlock this feature',
 *   onUpgrade: () => {
 *     // Custom logic before navigation
 *     console.log('User clicked upgrade');
 *   }
 * });
 * ```
 */
export function showSubscriptionModal(options?: SubscriptionModalOptions) {
  subscriptionModalManager.show(options);
}
